import React from 'react';
import { motion } from 'framer-motion';
import { AlertOctagon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ModuleDisabledScreenProps {
  /** Message du backend si dispo (module_disabled), sinon message par défaut */
  backendMessage?: string;
  storeName?: string;
  logoUrl?: string;
}

/**
 * v5 — Écran de blocage "module kiosk désactivé par l'admin".
 *
 * ⚠️ DISTINCT du RushBlockScreen :
 *  - Rush   = pause temporaire du restaurateur → compte à rebours, ton chaleureux
 *  - Module = blocage admin (impayé/suspension) → pas de compteur, "Contactez Restomi"
 *
 * Volontairement neutre et sobre (gris/ardoise) et non aux couleurs du thème :
 * ce n'est pas un état commercial normal, il ne doit pas ressembler à une promo.
 */
export const ModuleDisabledScreen: React.FC<ModuleDisabledScreenProps> = ({
  backendMessage,
  storeName,
  logoUrl,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[110] flex flex-col items-center justify-center px-8 py-10 overflow-y-auto bg-slate-900"
      role="alert"
      aria-live="assertive"
    >
      {/* En-tête store (optionnel) */}
      {(logoUrl || storeName) && (
        <div className="flex flex-col items-center gap-3 mb-10 opacity-60">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={storeName || ''}
              className="h-16 w-auto object-contain grayscale"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          {storeName && (
            <p className="text-lg font-medium tracking-wide text-slate-400">
              {storeName}
            </p>
          )}
        </div>
      )}

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="mb-8"
      >
        <div className="w-28 h-28 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center">
          <AlertOctagon className="text-slate-400" size={60} strokeWidth={1.8} />
        </div>
      </motion.div>

      <motion.div
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35, delay: 0.1 }}
        className="text-center max-w-2xl"
      >
        <h1 className="text-4xl font-bold text-white mb-5">
          {t('Service momentanément indisponible')}
        </h1>
        <p className="text-2xl text-slate-300 leading-relaxed">
          {backendMessage || t('Ce kiosk est désactivé. Merci de contacter Restomi.')}
        </p>
      </motion.div>
    </div>
  );
};

export default ModuleDisabledScreen;
